'use client';

import React, { FC, useEffect } from 'react';
import { useA11yState } from '@/hooks/useA11yState';

interface AccessibilityLinkProps {
  enableAccessibilityLabel: string;
  className?: string;
}

const AccessibilityLink: FC<AccessibilityLinkProps> = ({
  enableAccessibilityLabel,
  className,
}) => {
  const userwayAccount = process.env.NEXT_PUBLIC_USERWAY_ACCOUNT;
  const { setVideoAccessibility } = useA11yState();
  const showAccessibilityLink =
    userwayAccount && enableAccessibilityLabel?.trim().length > 0;

  // Handle Userway "Pause Animations" feature by setting accessibility mode
  useEffect(() => {
    if (!userwayAccount) return;

    const handleAccessibilityEnabled = () => {
      setVideoAccessibility(true);
    };

    const handleAccessibilityDisabled = () => {
      setVideoAccessibility(false);
    };

    document.addEventListener(
      'userway:feature_enabled_s13',
      handleAccessibilityEnabled,
    );
    document.addEventListener(
      'userway:feature_disabled_s13',
      handleAccessibilityDisabled,
    );

    return () => {
      document.removeEventListener(
        'userway:feature_enabled_s13',
        handleAccessibilityEnabled,
      );
      document.removeEventListener(
        'userway:feature_disabled_s13',
        handleAccessibilityDisabled,
      );
    };
  }, [userwayAccount, setVideoAccessibility]);

  if (!showAccessibilityLink) {
    return null;
  }

  // The "userway-accessibility-trigger" ID is used to trigger the Userway widget, see UserwaySnippet.tsx
  return (
    <button
      id="userway-accessibility-trigger"
      type="button"
      className={className ?? 'font-bold underline'}
      data-testid="userway-accessibility-link"
    >
      {enableAccessibilityLabel}
    </button>
  );
};

export default AccessibilityLink;
